import { QueryKey, useQuery } from "@tanstack/react-query";
import log from "loglevel";
import { useAuth } from "../useAuth";
import { sendRequest } from "./sendRequest";
import { UseApiQueryOptions } from "./types";
import { baseQueryOptions } from "@/core/hooks/useApiQueries/baseQueryOptions";

/**
 * O `queryFn` padrão das queries de API: a URL sai da própria `queryKey`
 * (`["sectors", id]` → `/sectors/:id`). Fora do hook para o pré-carregamento do
 * boot (SA-1452) montar a MESMA query sem passar por componente.
 */
export function apiQueryFn<TQueryFnData>(
  token: string | null | undefined,
  method: UseApiQueryOptions["method"],
  queryKey: QueryKey,
  params?: Record<string, string>
): () => Promise<TQueryFnData> {
  return async () => {
    try {
      const response = await sendRequest(token, {
        method,
        url: `/${queryKey.join("/")}`,
        params,
      });
      return response.data.data.attributes || response.data.data;
    } catch (error) {
      log.error(`Erro no request ${queryKey.join("/")}:`, error);
      throw error;
    }
  };
}

export function useApiQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({
  method,
  queryKey,
  params,
  ...otherOptions
}: UseApiQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const { token } = useAuth();

  return useQuery<TQueryFnData, TError, TData, TQueryKey>({
    // baseQueryOptions são os defaults; otherOptions (explícitos no call site)
    // sobrescrevem. queryKey/queryFn vêm por último e nunca são sobrescritos.
    ...baseQueryOptions,
    ...otherOptions,
    queryKey,
    queryFn: apiQueryFn<TQueryFnData>(token, method, queryKey, params),
  });
}
